/**
 * Newsletter.jsx — Email Newsletter Signup
 *
 * Redesigned tile matching the site's new aesthetic:
 * Titan One heading, Raleway 700 body, brand colors.
 * Submits first name + email to Square Customers API via Firebase Cloud Function.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useNewsletterContent } from '../services/firestore';

const inputStyle = {
  border: '1.5px solid #9C7A3C',
  borderRadius: 50,
  background: 'transparent',
  padding: '14px 24px',
  fontSize: '1rem',
  fontFamily: "'Raleway', sans-serif",
  fontWeight: 700,
  color: '#1a1208',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

export default function Newsletter() {
  const { newsletter: n } = useNewsletterContent();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Pre-warm subscribeNewsletter so submit is instant when the user clicks.
  useEffect(() => {
    httpsCallable(
      functions,
      'subscribeNewsletter',
    )({ warmup: true }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const subscribeNewsletter = httpsCallable(
        functions,
        'subscribeNewsletter',
      );
      await subscribeNewsletter({
        email: email.trim(),
        name: firstName.trim(),
      });
      setStatus('success');
      setFirstName('');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="section-pad">
      <div
        style={{
          padding: 'clamp(40px, 6vw, 64px) clamp(28px, 6vw, 64px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 32,
        }}
      >
        {/* Heading block */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: '1rem',
              color: '#9C7A3C',
              letterSpacing: '0.01em',
              margin: 0,
            }}
          >
            {n.label}
          </p>
          <h2
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(1.05rem, 1.8vw, 1.25rem)',
              color: '#1a1208',
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {n.heading}
          </h2>
          <p
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
              color: '#1a1208',
              lineHeight: 1.5,
              maxWidth: 400,
              margin: 0,
            }}
          >
            {n.description}
          </p>
        </div>

        {/* Form / success state */}
        {status === 'success' ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <p
              style={{
                fontFamily: "'Titan One', sans-serif",
                fontWeight: 400,
                fontSize: '1.5rem',
                color: '#1a1208',
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              {n.successHeading}
            </p>
            <p
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 700,
                fontSize: '1rem',
                color: '#9C7A3C',
                margin: 0,
              }}
            >
              {n.successMessage.replace('{name}', firstName || 'friend')}
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              width: '100%',
              maxWidth: 440,
            }}
          >
            <input
              type="text"
              placeholder={n.firstNamePlaceholder}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
              style={inputStyle}
            />
            <input
              type="email"
              placeholder={n.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={inputStyle}
            />

            {status === 'error' && (
              <p
                style={{
                  fontSize: '0.85rem',
                  color: '#b94a48',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontWeight: 700,
                fontSize: '1rem',
                background: 'transparent',
                color: '#9C7A3C',
                border: '1.5px solid #9C7A3C',
                borderRadius: 50,
                padding: '16px 36px',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                opacity: status === 'loading' ? 0.6 : 1,
                transition:
                  'border-color 0.2s ease, color 0.2s ease, transform 0.18s ease, box-shadow 0.18s ease',
                letterSpacing: '0.01em',
                marginTop: 4,
              }}
              onMouseEnter={(e) => {
                if (status === 'loading') return;
                e.target.style.borderColor = '#7A5E2E';
                e.target.style.color = '#7A5E2E';
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow =
                  '0 8px 24px rgba(156, 122, 60, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#9C7A3C';
                e.target.style.color = '#9C7A3C';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {status === 'loading' ? 'Sending…' : n.buttonText}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
